import useWindowSize from "./UseWindowHook";
import Itinerary from "../Data/Itinerary";
import StartWidget from "../Data/StartWidget";


// Hook used to determine what the height of the start widget should be in units of VH(desktop) or pixels(mobile)
const useCalculateWidgetHeight = (
  startWidget: StartWidget | undefined,
  itinerary: Itinerary,
  autoScroll: boolean,
  show: string,
  showModal?: string,
  isQuoteBooked?: boolean
) => {
  // to determine whether to use mobile or desktop calculations
  const {width} = useWindowSize();

  const showModalValue = showModal || "";

  // using avail height helps during times when the VH changes temporarily(add item modal)
  const height = window.screen.availHeight;
  
  let moduleAndButtonViewHeight = 0;
  let firstLoad = false;

  // these look like arbitrary values but they are designed
  // around 1080p 100% browser size for desktop
  // TODO - more robust future solution should be implemented
  // TODO - to scale based on all window sizes, consider a pixel-based
  // approach which is more portable
  const calculateWidgetViewHeight = () => {
    const MODULE_ITEM_SCALAR_VH = 22.5;
    const BASE_VH = 31;
    const ADD_ITEM_VH = 93;
    const NO_ITEM_VH = 38;
    const TABLET_WIDTH_THRESHOLD = 768;

    // the threshold for tablet view
    if (width <= TABLET_WIDTH_THRESHOLD) {
      return calculateMobileWidgetHeight();
    }

    // calculate total units to scale the view height by
    const {numItems} = calculateTotalItineraryUnits();

    // base viewHeight to calculate from
    let viewHeight = BASE_VH;
    
    // expand size for "add item" modal
    if (show !== "" && show !== "build-your-own") {
      return viewHeightScalar(ADD_ITEM_VH);
    }

    if (numItems === 0) {
      firstLoad = true;
      return viewHeightScalar(NO_ITEM_VH);
    } 
    
    viewHeight+=(MODULE_ITEM_SCALAR_VH*numItems);
    
    // if module and button viewheight has been calculated already
    // then skip re-calculation
    if (moduleAndButtonViewHeight == 0) {
      moduleAndButtonViewHeight = calculateTotalModuleAndButtonHeight();
    }
    viewHeight+=moduleAndButtonViewHeight;

    return viewHeightScalar(viewHeight);
  }

  const calculateMobileWidgetHeight = () => {
    const MODULE_SCALAR_PX = 220;
    const BASE_PX = 480;
    const ADD_ITEM_PX = 900;
    const NO_ITEM_PX = 510;
    const AT_LEAST_ONE_ITEM_PX = 535;
    const ITEMS_AFTER_ONE_SCALAR_PX = 140;
    const GET_QUOTE_PX = 900;
    const BOOK_NOW_PX = 1050;
    const REQUEST_QUOTE_PX = 1000;
    // TODO: this can be adjusted once the payment page is more mobile friendly
    const MAKE_PAYMENT_PX = 1200;
    const PACKAGE_SPECIALS_PX = 900;

    // try to do mobile in pixels to make it more transferable
    let viewHeight = BASE_PX;

    // expand size for "add item" modal, this supports two
    // full view elements being shown at once
    if (show.includes("add-")) {
      return ADD_ITEM_PX;
    }

    if (show === "get-quote") {
      return GET_QUOTE_PX;
    }

    if (show.includes("request-")) {
      return REQUEST_QUOTE_PX;
    }

    if (showModalValue === "make-payment") {
      return MAKE_PAYMENT_PX;
    }

    if (showModalValue === "book-now") {
      return BOOK_NOW_PX;
    }

    if (show === "package-specials") {
      return PACKAGE_SPECIALS_PX;
    }

    // calculate modules and total units to scale the view height
    const {modules, numItems} = calculateTotalItineraryUnits();

    if (numItems === 0) {
      // this height supports the calendar
      firstLoad = true;
      return NO_ITEM_PX;
    }

    // first item calculation is different because the shown fields change
    if (numItems >= 1) {
      viewHeight = AT_LEAST_ONE_ITEM_PX;
    }

    // each additional module after the first gets scaled linearly
    viewHeight+=((modules-1)*MODULE_SCALAR_PX);
    
    // pixels are already accounted for on the first item of each module
    viewHeight+=((numItems-modules)*ITEMS_AFTER_ONE_SCALAR_PX);

    // this adds extra padding to accommodate totals section
    if (isQuoteBooked) {
      viewHeight+=450;
    }

    return viewHeight;
  }

   // quick and dirty scalar solution to better support smaller screens
  const viewHeightScalar = (viewHeight: number) => {
    const TUNED_HEIGHT_PX = 1080;
    const MAGICAL_ADDITIVE = 1.2;

    // based on using 1080p as my tuned value
    if (height < TUNED_HEIGHT_PX) {
      // this is somewhat arbitrary but helps give smaller vertical screens better capability, even if its not tuned perfectly
      const lowHeightScalar = ((TUNED_HEIGHT_PX-height)/TUNED_HEIGHT_PX) + MAGICAL_ADDITIVE;
      viewHeight*=lowHeightScalar;
    }
    return viewHeight;
  }

  const calculateTotalModuleAndButtonHeight = () => {
    let viewHeight = 0;
    const MODULE_ADDITIVE_VH = 5;
    const BUTTON_ADDITIVE_VH = 3;
    const SAVE_BUTTON_ADDITIVE_VH = 5;

    // Module Calculations
    if (startWidget?.showCustomItemsModule) {
      viewHeight+=MODULE_ADDITIVE_VH;
    }
    if (startWidget?.showTransportationModule) {
      viewHeight+=MODULE_ADDITIVE_VH;
    }
    if (startWidget?.showLodgingModule) {
      viewHeight+=MODULE_ADDITIVE_VH;
    }
    if (startWidget?.showGolfModule) {
      viewHeight+=MODULE_ADDITIVE_VH;
    }

    // Button Calculations
    if (startWidget?.showExtrasButton) {
      viewHeight+=BUTTON_ADDITIVE_VH;
    }
    if (startWidget?.showGolfButton) {
      viewHeight+=BUTTON_ADDITIVE_VH;
    }
    if (startWidget?.showLodgingButton) {
      viewHeight+=BUTTON_ADDITIVE_VH;
    }
    if (startWidget?.showSaveQuoteButton) {
      viewHeight+=SAVE_BUTTON_ADDITIVE_VH;
    }
    
    return viewHeight;
  }

  const calculateTotalItineraryUnits = () => {
    // individual counts must be tracked since each type has its own itinerary type
    let golfCount = 0;
    let transportCount = 0;
    let lodgingCount = 0;
    let customCount = 0;
    let modules = 0;

    if (itinerary.golfItineraries !== undefined) {
      itinerary.golfItineraries.map(itinerary => {
        golfCount+=itinerary.itineraryUnits.length;
      })
      if (golfCount > 0) modules++;
    }
    if (itinerary.transportItineraries !== undefined) {
      itinerary.transportItineraries.map(itinerary => {
        transportCount+=itinerary.itineraryUnits.length;
      })
      if (transportCount > 0) modules++;
    }
    if (itinerary.lodgingItineraries !== undefined) {
      itinerary.lodgingItineraries.map(itinerary => {
        lodgingCount+=itinerary.itineraryUnits.length;
      })
      if (lodgingCount > 0) modules++;
    }
    if (itinerary.customItineraries !== undefined) {
      itinerary.customItineraries.map(itinerary => {
        customCount+=itinerary.itineraryUnits.length;
      })
      if (customCount > 0) modules++;
    }
    return {modules, numItems: (golfCount+transportCount+lodgingCount+customCount)};
  }

  const sendItineraryUnitsMessage = () => {
    const viewHeight = calculateWidgetViewHeight();
    window.parent.postMessage({viewHeight: viewHeight, autoScroll: autoScroll, isMobile: width<=768, firstLoad: firstLoad}, "*");
    firstLoad = false;
  }

  return {sendItineraryUnitsMessage}
}

export default useCalculateWidgetHeight;